import { useEffect, useState } from 'react'
import { resolveSession, type SessionState } from './session/session'
import {
  fetchAuthorization,
  type PartnerAuthorization,
} from './authz/partner'
import { mockAuthorization, mockSession } from './dev'

/**
 * Resolve session, then authorization, as one sequence. There is no
 * authorization question to ask until there is a verified principal to ask it
 * about, so authz stays `loading` until the session resolves verified.
 *
 * Authorization is re-evaluated on each app load and on principal change, and
 * is never cached across sessions (spec 4.3). `attempt` drives the error-retry
 * path; changing it re-runs the whole sequence.
 */
export function useGate(attempt = 0): {
  session: SessionState
  authz: PartnerAuthorization
} {
  const [session, setSession] = useState<SessionState>({ status: 'not_configured' })
  const [authz, setAuthz] = useState<PartnerAuthorization>({ status: 'loading' })

  useEffect(() => {
    let live = true
    setAuthz({ status: 'loading' })

    void (async () => {
      const s = mockSession() ?? (await resolveSession())
      if (!live) return
      setSession(s)

      if (s.status !== 'verified') {
        // No principal, so no authorization to resolve. Leave it loading; the
        // guard routes on the session state alone in every non-verified case.
        return
      }
      const a = mockAuthorization() ?? (await fetchAuthorization(s.principal))
      if (!live) return
      setAuthz(a)
    })()

    return () => {
      live = false
    }
  }, [attempt])

  return { session, authz }
}
